import type { GetCheckoutPurchaseIntentsByPurchaseIntentIdResponse } from "@birdwatch/client";

import type {
  PurchaseIntentCheckoutLineItem,
  PurchaseIntentPriceDetail,
} from "@/features/shared/checkout-summary";

import { SERVICE_CREDIT_KEY } from "./presale-offer";

/** `Product.type` of the membership row. Everything else the link sells is a service. */
const MEMBERSHIP_PRODUCT_TYPE = "PLAN";

/**
 * The reservation as this funnel reads it — ONE parse of the intent's payload blob.
 *
 * Nothing here is recomputed: `priceDetail` is the platform's own ledger and
 * `checkoutLineItems` is the cart it priced, both written by `getPurchaseIntentById` on
 * every read. This module deliberately contains no arithmetic — no sums, no subtraction,
 * no rounding. A checkout that reconstructs Stripe's coupon math is exactly what this
 * replaced.
 */
export type PresaleCart = {
  intentId: string;
  placeId: string;
  accountId: string;
  /** Top-level on the row: `getPurchaseIntentById` lifts it out of the payload. */
  flow: string;
  /** Set once the intent has been charged — the reservation is done. */
  stripeInvoiceId: string;
  /** The server's ledger. Every money row the summary card renders is a label off this. */
  priceDetail: PurchaseIntentPriceDetail | undefined;
  /** The PLAN row of the link's cart — the founding membership being reserved. */
  membership: PurchaseIntentCheckoutLineItem | undefined;
  /** Everything else the link sells, in the link's own order. */
  services: PurchaseIntentCheckoutLineItem[];
  /** The prepaid balance the campaign codes cover; absent when the link sells none. */
  credit: PurchaseIntentCheckoutLineItem | undefined;
  /** `Plan.planKey` — the same value the join funnel sends as `planKey`. */
  planKey: string;
  checkoutLinkId: string;
  discountCode: string;
  /** The non-membership product keys — the presale twin of Join's `extraAddOnKeys`. */
  addOnKeys: string[];
  paymentMethodId: string;
  /** `"card"` / `"us_bank_account"` — the analytics `paymentMethod` key, as Join sends it. */
  paymentMethodType: string | undefined;
};

/**
 * Parses the intent row into the cart this funnel renders.
 *
 * The `any` cast happens HERE and nowhere else: the generated client types `payload` as
 * `Record<string, unknown>` (see `features/shared/checkout-summary/purchase-intent-payload.ts`),
 * so every checkout consumer would otherwise cast for itself.
 *
 * Returns `undefined` for a missing row so the page's gate is a single `if`.
 */
export function readPresaleCart(
  purchaseIntent:
    | GetCheckoutPurchaseIntentsByPurchaseIntentIdResponse
    | undefined,
): PresaleCart | undefined {
  if (!purchaseIntent) return undefined;

  const payload: any = purchaseIntent.payload;
  // Absent on an intent with no checkout link, and on one written by a platform release
  // that predates the field — an empty cart, not a crash.
  const lineItems: PurchaseIntentCheckoutLineItem[] =
    payload?.checkoutLineItems ?? [];

  const membership = lineItems.find(
    (item) => item.product?.type === MEMBERSHIP_PRODUCT_TYPE,
  );
  const services = lineItems.filter(
    (item) => item.product?.type !== MEMBERSHIP_PRODUCT_TYPE,
  );

  return {
    intentId: purchaseIntent.id,
    placeId: purchaseIntent.placeId ?? "",
    accountId: purchaseIntent.accountId ?? "",
    flow: purchaseIntent.flow ?? "",
    stripeInvoiceId: purchaseIntent.stripeInvoiceId ?? "",
    priceDetail: payload?.priceDetail,
    membership,
    services,
    credit: services.find((item) => item.product?.key === SERVICE_CREDIT_KEY),
    // `add-my-place` stores `planKey`, and the enriched PLAN row carries the SAME value in
    // `product.key` (the platform substitutes the plan for the link's plan-type product),
    // so the fallback cannot disagree with the stored key.
    planKey: payload?.planKey ?? membership?.product?.key ?? "",
    checkoutLinkId: payload?.checkoutLinkId ?? "",
    discountCode: payload?.discountCode ?? "",
    addOnKeys: services
      .map((item) => item.product?.key)
      .filter((key): key is string => Boolean(key)),
    paymentMethodId: payload?.paymentMethodId ?? "",
    paymentMethodType: payload?.paymentMethodDetail?.type,
  };
}
